import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Divider,
  Typography,
  Space,
  Alert,
  Spin,
  Statistic,
  Row,
  Col,
  List,
  Tag,
  Empty
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  SaveOutlined,
  HeartFilled,
  GiftOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import authService from '../../services/authService';
import api from '../../services/api';
import TwoFactorSettings from '../../components/auth/TwoFactorSettings';
import PhoneInput, { validatePhoneNumber } from '../../components/PhoneInput';
import './ProfilePage.scss';

const { Title, Text } = Typography;

const ProfilePage = observer(() => {
  const { t } = useTranslation();
  const { authStore } = useStores();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donationStats, setDonationStats] = useState(null);
  const [donationLoading, setDonationLoading] = useState(false);

  useEffect(() => {
    loadProfile();
    loadDonationStats();
  }, []);

  const loadDonationStats = async () => {
    try {
      setDonationLoading(true);
      const response = await api.get('/orders/orders/donations/');
      setDonationStats(response.data);
    } catch (err) {
      console.error('Failed to load donation stats:', err);
    } finally {
      setDonationLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.getProfile();
      setProfileData(data);
      profileForm.setFieldsValue({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
      });
    } catch (err) {
      setError(t('profile.info.loadFailed'));
      message.error(t('profile.info.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    try {
      setProfileLoading(true);
      const updatedData = await authService.updateProfile({
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number || '',
      });

      // Update the auth store with new user data
      authStore.updateUser(updatedData);
      setProfileData(updatedData);

      message.success(t('profile.info.updateSuccess'));
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      message.error(errorMessage);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    try {
      setPasswordLoading(true);
      await authService.changePassword(
        values.old_password,
        values.new_password,
        values.confirm_password
      );
      message.success(t('profile.password.changeSuccess'));
      passwordForm.resetFields();
    } catch (err) {
      const errorMessage =
        err.response?.data?.old_password?.[0] ||
        err.response?.data?.new_password?.[0] ||
        err.response?.data?.new_password2?.[0] ||
        err.response?.data?.message ||
        t('profile.password.changeFailed');
      message.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="page-header">
        <Title level={2}>{t('profile.title')}</Title>
        <Text type="secondary">{t('profile.subtitle')}</Text>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <div className="profile-content">
        {/* Profile Information Card */}
        <Card title={t('profile.info.title')} className="profile-card">
          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleUpdateProfile}
            requiredMark={false}
          >
            <Form.Item
              label={t('common.email')}
              name="email"
            >
              <Input
                prefix={<MailOutlined />}
                disabled
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('auth.register.firstName')}
              name="first_name"
              rules={[
                { required: true, message: t('profile.info.firstNameRequired') },
                { max: 150, message: t('profile.info.firstNameMaxLength') }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('profile.info.firstNamePlaceholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('auth.register.lastName')}
              name="last_name"
              rules={[
                { required: true, message: t('profile.info.lastNameRequired') },
                { max: 150, message: t('profile.info.lastNameMaxLength') }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('profile.info.lastNamePlaceholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('common.phone')}
              name="phone_number"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (validatePhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.register.phoneInvalid')));
                  },
                },
              ]}
            >
              <PhoneInput
                placeholder={t('auth.register.phonePlaceholder')}
              />
            </Form.Item>

            {profileData && (
              <Space direction="vertical" size={8} style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  <strong>{t('profile.info.accountType')}</strong> {profileData.role}
                </Text>
                {profileData.date_joined && (
                  <Text type="secondary">
                    <strong>{t('profile.info.memberSince')}</strong> {new Date(profileData.date_joined).toLocaleDateString()}
                  </Text>
                )}
              </Space>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={profileLoading}
                size="large"
              >
                {t('profile.info.updateButton')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Divider />

        {/* Two-Factor Authentication */}
        <TwoFactorSettings />

        <Divider />

        {/* NGO Donations Card - Show for both clients and partners */}
        {(authStore.user?.role === 'CLIENT' || authStore.user?.role === 'PARTNER') && (
          <Card
            title={
              <Space>
                <HeartFilled style={{ color: '#eb2f96' }} />
                <span>{t('profile.donations.title')}</span>
              </Space>
            }
            className="donations-card"
            style={{ marginBottom: 24 }}
          >
            {donationLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : donationStats && parseFloat(donationStats.total_donated) > 0 ? (
              <>
                <Row gutter={24} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12}>
                    <Statistic
                      title={t('profile.donations.totalDonated')}
                      value={parseFloat(donationStats.total_donated).toFixed(2)}
                      prefix={<HeartFilled style={{ color: '#eb2f96' }} />}
                      suffix="€"
                      valueStyle={{ color: '#eb2f96' }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Statistic
                      title={t('profile.donations.ordersWithDonations')}
                      value={donationStats.donation_count}
                      prefix={<GiftOutlined style={{ color: '#52c41a' }} />}
                    />
                  </Col>
                </Row>

                {donationStats.ngo_breakdown && donationStats.ngo_breakdown.length > 0 && (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 12 }}>
                      {t('profile.donations.byNgo')}
                    </Text>
                    <List
                      size="small"
                      dataSource={donationStats.ngo_breakdown}
                      renderItem={(item) => (
                        <List.Item
                          extra={
                            <Tag color="pink" style={{ fontSize: 14 }}>
                              €{parseFloat(item.total_donated).toFixed(2)}
                            </Tag>
                          }
                        >
                          <Space>
                            <HeartFilled style={{ color: '#eb2f96' }} />
                            <span>{item.ngo_name}</span>
                            <Text type="secondary">({item.order_count} {t('profile.donations.orders')})</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </>
                )}

                <Alert
                  message={t('profile.donations.thankYou')}
                  type="success"
                  showIcon
                  icon={<HeartFilled />}
                  style={{ marginTop: 16 }}
                />
              </>
            ) : (
              <Empty
                image={<HeartFilled style={{ fontSize: 48, color: '#d9d9d9' }} />}
                description={
                  <Space direction="vertical" size={4}>
                    <Text>{t('profile.donations.noDonationsYet')}</Text>
                    <Text type="secondary">{t('profile.donations.noDonationsHint')}</Text>
                  </Space>
                }
              />
            )}
          </Card>
        )}

        <Divider />

        {/* Change Password Card */}
        <Card title={t('profile.password.title')} className="password-card">
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
            requiredMark={false}
          >
            <Form.Item
              label={t('profile.password.currentLabel')}
              name="old_password"
              rules={[
                { required: true, message: t('profile.password.currentRequired') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('profile.password.currentPlaceholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('profile.password.newLabel')}
              name="new_password"
              rules={[
                { required: true, message: t('profile.password.newRequired') },
                { min: 8, message: t('profile.password.newMinLength') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('profile.password.newPlaceholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('profile.password.confirmLabel')}
              name="confirm_password"
              dependencies={['new_password']}
              rules={[
                { required: true, message: t('profile.password.confirmRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('profile.password.confirmMismatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('profile.password.confirmPlaceholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<LockOutlined />}
                loading={passwordLoading}
                size="large"
              >
                {t('profile.password.changeButton')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
});

export default ProfilePage;
